import { getJwtToken, initializeJwtToken } from '../modules/jwtTokenManager.js';

const WS_DEBUG = false;
const MAX_TOKEN_RETRIES = 5;
const MAX_RETRY_DELAY = 30;
let tokenInitializationPromise = null;
const requestedWebSocketChannels = new Set();

function sendWebSocketRpc(ws, payload) {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
    }

    try {
        ws.send(JSON.stringify(payload));
    } catch (error) {
        wsLog('[WebSocket] Failed to send RPC', error);
    }
}

function subscribeToChannel(ws, channel) {
    if (!channel) {
        return;
    }

    sendWebSocketRpc(ws, {
        id: `subscribe:${channel}:${Date.now()}`,
        method: 'subscribe',
        parameters: [channel],
    });
}

function queueWebSocketChannelSubscription(channel) {
    if (!channel) {
        return;
    }

    requestedWebSocketChannels.add(channel);

    // Channels added to the set are subscribed on (re)connect via
    // subscribeToRequestedChannels. This same-tab signal lets a socket that is
    // already open subscribe live, since currentWs is local to $(document).ready.
    document.dispatchEvent(new CustomEvent('ws:subscribe-channel-request', {
        detail: { channel }
    }));
}

window.requestWebSocketChannelSubscription = queueWebSocketChannelSubscription;

function subscribeToRequestedChannels(ws) {
    requestedWebSocketChannels.forEach((channel) => {
        subscribeToChannel(ws, channel);
    });
}

async function getOrInitializeJwtToken() {
    const existingTokenData = getJwtToken();
    if (existingTokenData?.auth?.token) {
        return existingTokenData;
    }

    if (!tokenInitializationPromise) {
        tokenInitializationPromise = initializeJwtToken().finally(() => {
            tokenInitializationPromise = null;
        });
    }

    return tokenInitializationPromise;
}

function wsLog(message, data) {
    if (WS_DEBUG) {
        if (data !== undefined) {
            console.log(message, data);
        } else {
            console.log(message);
        }
    }
}

// Tab title unread indicator: prepends "(N) " to document.title where N is
// the total count of *new* events (PMs, wall posts, friend requests, photo
// comments, note comments) that arrived since the user last looked at the
// tab — not the lifetime unread total. Matches Gmail/Slack/Discord: the
// badge answers "what's new since I looked away?", not "how big is my
// backlog?".
//
// "Not actively looking" means either:
//   - The tab is hidden (different tab in this window), or
//   - The window has lost OS focus (different window/app is active)
// The second case matters because Chrome keeps document.hidden=false for the
// front tab of a backgrounded window, so a hidden-only check misses the
// common multi-window workflow.
//
// Implementation: per-counter state. When the user looks away, snapshot the
// current value of each counter as its baseline. The badge shows the sum of
// (latest - baseline) across all tracked counters. When the user returns,
// clear baselines and re-snapshot on next blur.
const TRACKED_COUNTER_IDS = ['pmNewCnt', 'wpNewCnt', 'frNewCnt', 'ncNewCnt', 'phNewCnt'];

(function initUnreadTitleIndicator() {
    let baseTitle = null;
    const latestCount = Object.fromEntries(TRACKED_COUNTER_IDS.map((id) => [id, 0]));
    const baselineCount = Object.fromEntries(TRACKED_COUNTER_IDS.map((id) => [id, null]));

    // Strip any existing "(N) " prefix so re-renders don't compound.
    const stripPrefix = (title) => title.replace(/^\(\d+\)\s+/, '');

    const captureBaseTitle = () => {
        if (baseTitle === null) {
            baseTitle = stripPrefix(document.title);
        }
    };

    const totalDelta = () => TRACKED_COUNTER_IDS.reduce((sum, id) => {
        const base = baselineCount[id];
        return base === null ? sum : sum + Math.max(0, latestCount[id] - base);
    }, 0);

    const render = () => {
        if (baseTitle === null) {
            return;
        }
        const delta = totalDelta();
        document.title = delta > 0 ? `(${delta}) ${baseTitle}` : baseTitle;
    };

    // On /messages the page manages its own unread state (and knows whether
    // the user is viewing the active thread), so defer to it entirely.
    const isMessagesPage = () => /\/app\/messages(?:$|\/|\?)/.test(
        window.location.pathname + window.location.search
    );

    const isUserLookingAtTab = () => !document.hidden && document.hasFocus();

    const snapshotBaselinesIfNeeded = () => {
        // Take baseline the first time the user looks away. Subsequent counter
        // updates while still away accumulate against this baseline.
        for (const id of TRACKED_COUNTER_IDS) {
            if (baselineCount[id] === null) {
                baselineCount[id] = latestCount[id];
            }
        }
    };

    const clearAndResetBaselines = () => {
        for (const id of TRACKED_COUNTER_IDS) {
            baselineCount[id] = null;
        }
        // Also halt jq-common's flashTitle setInterval if it's running, so its
        // 1s tick doesn't immediately overwrite the cleared title.
        if (typeof window.stopFlashTitle === 'function') {
            window.stopFlashTitle();
        }
        if (baseTitle !== null) {
            render();
        }
    };

    document.addEventListener('ws:counter-update', (event) => {
        if (!event.detail || !TRACKED_COUNTER_IDS.includes(event.detail.id)) {
            return;
        }
        const value = parseInt(event.detail.value, 10);
        if (!Number.isFinite(value)) {
            return;
        }
        latestCount[event.detail.id] = value;

        if (isMessagesPage() || isUserLookingAtTab()) {
            // User is here — the badge stays empty and the next blur snapshots
            // these latest values as the baselines.
            return;
        }

        captureBaseTitle();
        snapshotBaselinesIfNeeded();
        render();
    });

    document.addEventListener('visibilitychange', () => {
        if (isUserLookingAtTab()) {
            clearAndResetBaselines();
        } else {
            // Tab just went hidden — lock in the baselines so any subsequent
            // arrivals show as a delta from this moment.
            captureBaseTitle();
            snapshotBaselinesIfNeeded();
        }
    });

    // Window focus/blur covers the case where the tab is the front tab in a
    // background window — visibilityState stays "visible" but the user
    // isn't actually looking at it until the window gains focus.
    window.addEventListener('focus', () => {
        if (isUserLookingAtTab()) {
            clearAndResetBaselines();
        }
    });

    window.addEventListener('blur', () => {
        if (!isUserLookingAtTab()) {
            captureBaseTitle();
            snapshotBaselinesIfNeeded();
        }
    });
})();

window["prepareNotification"] = function (entity) {
    // skip THREAD_SYNC_MESSAGE and FEED_SYNC_MESSAGE types
    if (entity.type === 'THREAD_SYNC_MESSAGE' || entity.type === 'FEED_SYNC_MESSAGE') {
        return null;
    }

    let data = entity.data,
        sender = entity.sender,
        cdn = '//ipstatic.net/';

    // default value When Sender is null...
    let thumb = cdn + 'images/male-empty-profile.png';

    let notification = {}

    if (sender) {
        if (sender.avatar) {
            thumb = sender.avatar.thumb_small;
        } else if (sender.sex) {
            thumb = cdn + 'images/' + sender.sex + '-empty-profile.png';
        }

        notification = {
            url: '/' + sender.username,
            username: sender.username,
            img_url: thumb,
            img_dims: "width='50' height='50'",
        };
    }

    /**
     * We don't use some notifications because it's rly hard to support in legacy:
     *
     * FEED_SYNC_MESSAGE
     * THREAD_SYNC_MESSAGE
     */
    switch (entity.type) {
        case 'COMMENT_MESSAGE':
            notification.title = window.t ? window.t('common.push_new_feed_comment') : 'New comment on your feed';
            notification.text = data.message;

            return notification;
        case 'COMMENT_REPLY_MESSAGE':
            notification.title = window.t ? window.t('common.push_comment_reply') : 'New reply to your comment';
            notification.text = data.message;

            return notification;
        case 'FRIEND_ACCEPT_MESSAGE':
            notification.title = window.t ? window.t('common.push_friend_accepted', {username: sender.username}) : sender.username + ' accepted your friendship!';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'BIRTHDAY_MESSAGE':
            notification.title = window.t ? window.t('common.push_birthday_today', {username: sender.username}) : sender.username + '`s birthday today';
            notification.text = window.t ? window.t('common.push_congratulate_birthday') : "Congratulate your friend on birthday!";

            return notification;
        case 'FRIEND_JOIN_MESSAGE':
            notification.title = window.t ? window.t('common.push_friend_joined', {username: sender.username}) : sender.username + ' just joined';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'FRIEND_REQUEST_MESSAGE':
            notification.title = window.t ? window.t('common.push_friend_request', {username: sender.username}) : sender.username + ' requested your friendship';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'LOGIN_NOTIFICATION':
            notification.title = window.t ? window.t('common.push_user_online', {username: sender.username}) : sender.username + ' is online';
            notification.text = window.t ? window.t('common.push_say_hi') : "Say 'Hi'!";

            return notification;
        case 'LIKE_MESSAGE':
            // @todo
            break;
        case 'PHOTO_COMMENT':
            notification.url = '/app/photo?pid=' + data.target_id;
            notification.title = window.t ? window.t('common.push_new_photo_comment', {username: sender.username}) : 'New photo comment from ' + sender.username;
            notification.text = data.snippet ? data.snippet : data.message;

            return notification;
        case 'THREAD_NEW_MESSAGE':
            notification.url = '/app/messages?thread_id=' + data.thread_id;
            notification.thread_id = data.thread_id;
            notification.title = window.t ? window.t('common.push_new_message', {username: sender.username}) : 'New message from ' + sender.username;
            notification.text = data.snippet ? data.snippet : data.message;

            document.dispatchEvent(new CustomEvent('thread:newmessage', {
                detail: { thread_id: String(data.thread_id) }
            }));

            return notification;
        case 'THREAD_SYNC_DELETED':
            // A thread was deleted on another device, or removed because the
            // other user blocked us. Let the PM page drop the row in place.
            document.dispatchEvent(new CustomEvent('thread:deleted', {
                detail: { thread_id: String(data.thread_id) }
            }));
            return null;
        case 'THREAD_VIEWED':
            // @todo: handle in pm
            break;

        case 'THREAD_TYPING':
            document.dispatchEvent(new CustomEvent('thread:typing', {
                detail: { thread_id: String(data.thread_id), sender: sender }
            }));
            return null;

        case 'THREAD_MESSAGE_REACTION':
            // IDs kept as strings (they exceed Number.MAX_SAFE_INTEGER).
            document.dispatchEvent(new CustomEvent('thread:reaction', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id),
                    reactor_uid: String(data.reactor_uid),
                    reaction: data.reaction,
                    action: data.action,
                    reactions: data.reactions || []
                }
            }));
            return null;

        case 'THREAD_MESSAGE_RECALLED':
            // A message was unsent. Hand the PM page the ids so it can remove the
            // open bubble live. IDs stay strings (they exceed
            // Number.MAX_SAFE_INTEGER).
            document.dispatchEvent(new CustomEvent('thread:recalled', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id)
                }
            }));
            return null;

        case 'WALL_MESSAGE':
            notification.url = '/app/wall';
            notification.title = window.t ? window.t('common.push_new_wall_post') : 'New post on your wall';
            notification.img_url = data.thumb;
            notification.text = data.snippet ? data.snippet : data.message;

            return notification;
    }

    return null;
}

window["escapeHtml"] = function (text) {
    var map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };

    return text.replace(/[&<>"']/g, function (m) {
        return map[m];
    });
}

window["onMessage"] = function (wsMessage) {
    let data;
    try {
        data = JSON.parse(wsMessage.data);

        if (!data || typeof data !== 'object') {
            throw new Error('Invalid message format');
        }
    } catch (e) {
        wsLog('Failed to parse WebSocket message', e);
        return;
    }

    if (data.type === 'COUNTER_UPDATE') {
        // Server payload shape: { type: 'COUNTER_UPDATE', entity: { id, value } }
        const counterId = data.entity?.id;
        const counterValue = data.entity?.value;

        if (!counterId || typeof counterId !== 'string') {
            wsLog('Invalid COUNTER_UPDATE message - missing or invalid id');
            return;
        }

        if (TRACKED_COUNTER_IDS.includes(counterId)) {
            $('#' + counterId).text('+' + counterValue).show();
            document.dispatchEvent(new CustomEvent('ws:counter-update', {
                detail: { id: counterId, value: counterValue }
            }));
        }
    } else if (data.type === 'GLOBAL_FEED_POST_ADDED') {
        document.dispatchEvent(new CustomEvent('ws:global-feed-post-added', {
            detail: data,
        }));
    } else {
        const notification = prepareNotification(data);

        if (notification && window.enable_notifs) {
            window.showNotifications([notification])
        }
    }
}

$(document).ready(function () {
    var retryCount = 0;
    var tokenRetryCount = 0;
    var isConnecting = false;
    var currentWs = null;

    var connect = async function () {
        if (isConnecting || currentWs) {
            wsLog('[WebSocket] Already connecting or connected, skipping');
            return false;
        }

        // Check if user is logged in by looking for user-id meta tag
        const userIdMeta = document.querySelector('meta[name="user-id"]');
        if (!userIdMeta || !userIdMeta.getAttribute('content')) {
            wsLog('[WebSocket] Not logged in, skipping connection');
            return false;
        }

        wsLog('[WebSocket] Starting connection attempt');
        isConnecting = true;

        var wspath = 'wss://api.interpals.net/v1/ws';

        if (document.location.hostname === 'localhost' || /^local(-\d+)?\.interpals\.net$/.test(document.location.hostname)) {
            var wsProto = document.location.protocol === 'https:' ? 'wss://' : 'ws://';
            wspath = wsProto + document.location.host + '/v1/ws';
        }

        // Get JWT token from localStorage
        const tokenData = await getOrInitializeJwtToken();
        if (!tokenData || !tokenData.auth || !tokenData.auth.token) {
            isConnecting = false;
            if (tokenRetryCount >= MAX_TOKEN_RETRIES) {
                wsLog('[WebSocket] Token not available after max retries, giving up');
                return false;
            }
            wsLog('[WebSocket] No token available yet, retrying in 1 second');
            tokenRetryCount++;
            setTimeout(function() {
                connect();
            }, 1000);
            return false;
        }

        tokenRetryCount = 0;
        wspath += '?token=' + tokenData.auth.token;

        var ws = new WebSocket(wspath);
        currentWs = ws;

        ws.onopen = function () {
            retryCount = 0;
            isConnecting = false;
            wsLog('[WebSocket] Connected successfully');
            subscribeToRequestedChannels(ws);
            document.dispatchEvent(new CustomEvent('ws:connected'));
        };

        ws.onmessage = onMessage;

        ws.onclose = function (event) {
            currentWs = null;
            isConnecting = false;
            document.dispatchEvent(new CustomEvent('ws:disconnected'));

            var retryTimeout = Math.min(Math.pow(2, retryCount), MAX_RETRY_DELAY);
            retryCount++;

            wsLog('[WebSocket] Connection closed (code: ' + event.code + '), retrying in ' + retryTimeout + ' seconds');

            setTimeout(
                function () {
                    connect()
                },
                retryTimeout * 1000
            );
        };

        ws.onerror = function (error) {
            wsLog('[WebSocket] Connection error', error);
            isConnecting = false;
        };
    };

    function cleanup() {
        if (currentWs) {
            wsLog('[WebSocket] Cleaning up connection');
            currentWs.close(1000, 'Page unload');
            currentWs = null;
        }
        isConnecting = false;
        retryCount = 0;
        tokenRetryCount = 0;
    }

    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);

    // A channel requested while this tab's socket is already open is subscribed
    // live. A channel requested before connect is covered by the
    // requestedWebSocketChannels set, which onopen flushes via
    // subscribeToRequestedChannels.
    document.addEventListener('ws:subscribe-channel-request', function (event) {
        const channel = event?.detail?.channel;

        if (channel && currentWs && currentWs.readyState === WebSocket.OPEN) {
            subscribeToChannel(currentWs, channel);
        }
    });

    connect();
});
